import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type PopStyle = {
	top: number;
	left: number;
	width: number;
};

type Props = {
	src: string;
	alt: string;
	className?: string;
	/** Larger preview in a fixed layer on hover (matches DocumotoTestReact `AuthenticatedLibraryImage`). */
	hoverPopout?: boolean;
	hoverPopoutPlacement?: 'default' | 'left';
};

const POP_LEAVE_MS = 140;

/**
 * Thumbnail + hover zoom popout for data URLs / normal image URLs (Fusion API already embeds part thumbnails).
 */
export function DocumotoHoverPopoutImage({
	src,
	alt,
	className = '',
	hoverPopout = false,
	hoverPopoutPlacement = 'default',
}: Props) {
	const popId = useId();
	const wrapRef = useRef<HTMLDivElement>(null);
	const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [popOpen, setPopOpen] = useState(false);
	const [popStyle, setPopStyle] = useState<PopStyle | null>(null);

	const clearLeaveTimer = useCallback(() => {
		if (leaveTimerRef.current != null) {
			clearTimeout(leaveTimerRef.current);
			leaveTimerRef.current = null;
		}
	}, []);

	const updatePopPosition = useCallback(() => {
		const el = wrapRef.current;
		if (!el) {
			return;
		}
		const r = el.getBoundingClientRect();
		const maxPop = Math.min(280, Math.max(120, window.innerWidth - 16));
		const pad = 8;

		if (hoverPopoutPlacement === 'left') {
			const popHeightEst = Math.min(256, window.innerHeight - 2 * pad);
			let left = r.left - maxPop - pad;
			let top = r.top + r.height / 2 - popHeightEst / 2;

			if (left < pad) {
				left = r.right + pad;
			}
			if (left + maxPop > window.innerWidth - pad) {
				left = Math.max(pad, window.innerWidth - maxPop - pad);
			}
			if (top + popHeightEst > window.innerHeight - pad) {
				top = window.innerHeight - popHeightEst - pad;
			}
			if (top < pad) {
				top = pad;
			}

			setPopStyle({ top, left, width: maxPop });
			return;
		}

		const estH = Math.min(260, window.innerHeight - 2 * pad);
		let left = r.left;
		let top = r.bottom + pad;

		if (left + maxPop > window.innerWidth - pad) {
			left = window.innerWidth - maxPop - pad;
		}
		if (left < pad) {
			left = pad;
		}

		if (top + estH > window.innerHeight - pad) {
			top = r.top - estH - pad;
		}
		if (top < pad) {
			top = pad;
		}

		setPopStyle({ top, left, width: maxPop });
	}, [hoverPopoutPlacement]);

	const openPop = useCallback(() => {
		clearLeaveTimer();
		if (!hoverPopout) {
			return;
		}
		updatePopPosition();
		setPopOpen(true);
	}, [clearLeaveTimer, hoverPopout, updatePopPosition]);

	const scheduleClosePop = useCallback(() => {
		clearLeaveTimer();
		leaveTimerRef.current = setTimeout(() => {
			leaveTimerRef.current = null;
			setPopOpen(false);
			setPopStyle(null);
		}, POP_LEAVE_MS);
	}, [clearLeaveTimer]);

	const keepPopOpen = useCallback(() => {
		clearLeaveTimer();
	}, [clearLeaveTimer]);

	useEffect(() => {
		if (!popOpen) {
			return;
		}
		const onScrollOrResize = () => {
			updatePopPosition();
		};
		window.addEventListener('scroll', onScrollOrResize, true);
		window.addEventListener('resize', onScrollOrResize);
		return () => {
			window.removeEventListener('scroll', onScrollOrResize, true);
			window.removeEventListener('resize', onScrollOrResize);
		};
	}, [popOpen, updatePopPosition]);

	useEffect(() => {
		return () => {
			clearLeaveTimer();
		};
	}, [clearLeaveTimer]);

	const imgEl = <img className={className || undefined} src={src} alt={alt} loading="lazy" decoding="async" />;

	if (!hoverPopout) {
		return imgEl;
	}

	const popLayer =
		popOpen && popStyle && typeof document !== 'undefined' ? (
			<div
				id={popId}
				className="documoto-hover-pop"
				role="tooltip"
				style={{
					position: 'fixed',
					top: popStyle.top,
					left: popStyle.left,
					width: popStyle.width,
					zIndex: 1100,
				}}
				onMouseEnter={keepPopOpen}
				onMouseLeave={scheduleClosePop}
			>
				<img className="documoto-hover-pop-img" src={src} alt="" decoding="async" />
			</div>
		) : null;

	return (
		<>
			<div ref={wrapRef} className="documoto-img-hover-wrap" onMouseEnter={openPop} onMouseLeave={scheduleClosePop}>
				{imgEl}
			</div>
			{popLayer ? createPortal(popLayer, document.body) : null}
		</>
	);
}
